// frontend/src/components/chat/chatMediaConstants.js
// Constantes centralizadas para multimedia del chat (notas de voz, video notes,
// llamadas). Evita "números mágicos" dispersos por los componentes.

// ---- Notas de voz ----
export const VOICE_MAX_SECONDS = 300;          // 5 minutos máx por nota de voz
export const WAVEFORM_PEAKS = 48;              // nº de picos del waveform (40-64)
export const VOICE_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
];

// ---- Notas de video ----
export const VIDEO_NOTE_MAX_SECONDS = 60;      // 60s máx por nota de video
export const VIDEO_NOTE_MAX_BYTES = 60 * 1024 * 1024; // 60MB (límite backend)
export const VIDEO_MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

// ---- Llamadas ----
export const CALL_CONNECT_TIMEOUT_MS = 30000;  // si no conecta en 30s → failed
export const CALL_MAX_PARTICIPANTS = 8;

// Razones de fin de llamada (alineadas con el backend).
// Se expone como funcion que recibe `t` para reaccionar a cambios de idioma.
export const getCallEndReasonLabels = (t) => ({
  hangup: t('hardcoded.components_chat_chatmediaconstants.llamada_finalizada'),
  rejected: t('hardcoded.pages_chatpage.llamada_rechazada'),
  ended_for_all: t('hardcoded.components_chat_chatmediaconstants.la_llamada_fue_terminada_para'),
  timeout: t('hardcoded.components_chat_calloverlay.sin_respuesta'),
  ended: t('hardcoded.components_chat_chatmediaconstants.llamada_finalizada'),
  cancelled: t('hardcoded.components_chat_chatmediaconstants.llamada_cancelada'),
  missed: t('hardcoded.components_chat_calloverlay.sin_respuesta'),
  failed: t('hardcoded.components_chat_chatmediaconstants.no_se_pudo_conectar_la'),
});

// Elige el primer MIME soportado por MediaRecorder de una lista de candidatos.
export const pickSupportedMime = (candidates) => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return '';
  for (const c of candidates) {
    try { if (window.MediaRecorder.isTypeSupported(c)) return c; } catch { /* noop */ }
  }
  return '';
};

// Decodifica un blob de audio y calcula un array de picos normalizados 0..1.
// Robusto: si decodeAudioData falla (formato no soportado), devuelve null.
export const computeWaveformFromBlob = async (blob, peaks = WAVEFORM_PEAKS) => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const arrayBuf = await blob.arrayBuffer();
    const audioBuf = await ctx.decodeAudioData(arrayBuf.slice(0));
    const raw = audioBuf.getChannelData(0);
    const block = Math.floor(raw.length / peaks) || 1;
    const out = [];
    let max = 0;
    for (let i = 0; i < peaks; i += 1) {
      let sum = 0;
      const start = i * block;
      for (let j = 0; j < block; j += 1) {
        const v = raw[start + j] || 0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / block); // energía del bloque
      out.push(rms);
      if (rms > max) max = rms;
    }
    try { ctx.close(); } catch { /* noop */ }
    // Normaliza 0..1 con un piso para que el silencio total no quede invisible.
    return out.map((v) => (max > 0 ? Math.min(1, Math.max(0.04, v / max)) : 0.04));
  } catch {
    return null;
  }
};
